import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useContent } from '../hooks/useContent';
import Hero from '../components/ui/Hero';
import SEO from '../components/SEO';

const ServicePage = ({ identifier }) => {
  const { t, getLocalizedPath } = useLanguage();
  const { data: pagesData, loading: pagesLoading } = useContent('pages_content');
  const { data: servicesData, loading: servicesLoading } = useContent('services');
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  
  const page = pagesData?.find(p => p.identifier === identifier);
  const service = servicesData?.find(s => s.page_slug === identifier);

  if (pagesLoading || servicesLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  const contentMap = {
    'service': {
      da: [
        'Rail & Maskinteknik ApS tilbyder professionel servicering og drift af skinnekørende arbejdsmaskiner. Vi har mange års erfaring med drift og vedligeholdelse af skinnekørende materiel, og vores speciale er netop skinnekørende arbejdsmaskiner, også kendt som Yellow Fleet.',
        'Vi sikrer, at jeres maskiner altid er driftssikre og overholder alle tekniske krav. Gennem vores store netværk i hele Europa kan vi hurtigt skaffe reservedele og specialviden, hvilket minimerer nedetid og sikrer en effektiv drift.',
        'Vi tilbyder kompetent service og rådgivning, der sikrer optimal drift af jeres materiel. Vi finder altid en vej til at løse jeres tekniske udfordringer, uanset om det drejer sig om løbende vedligeholdelse eller akutte reparationer.',
        'Har I idéen, så har vi løsningen – vi kombinerer vores tekniske ekspertise med en praktisk tilgang for at levere de bedste resultater for vores kunder.'
      ],
      en: [
        'Rail & Maskinteknik ApS offers professional servicing and operation of rail-bound work machines. We have many years of experience in the operation and maintenance of rail-bound equipment, and our specialty is precisely rail-bound work machines, also known as Yellow Fleet.',
        'We ensure that your machines are always reliable and comply with all technical requirements. Through our large network in Europe, we can quickly obtain spare parts and specialized knowledge, minimizing downtime and ensuring efficient operation.',
        'We offer competent service and advice that ensures optimal operation of your equipment. We always find a way to solve your technical challenges, whether it involves ongoing maintenance or urgent repairs.',
        'If you have the idea, we have the solution – we combine our technical expertise with a practical approach to deliver the best results for our customers.'
      ],
      de: [
        'Rail & Maskinteknik ApS bietet professionellen Service und Betrieb von schienengebundenen Arbeitsmaschinen. Wir verfügen über langjährige Erfahrung im Betrieb und in der Wartung von schienengebundenen Fahrzeugen, und unsere Spezialisierung liegt genau auf schienengebundenen Arbeitsmaschinen, auch bekannt als Yellow Fleet.',
        'Wir sorgen dafür, dass Ihre Maschinen stets zuverlässig sind und alle technischen Anforderungen erfüllen. Durch unser großes Netzwerk in ganz Europa können wir schnell Ersatzteile und Fachwissen beschaffen, was Ausfallzeiten minimiert und einen effizienten Betrieb gewährleistet.',
        'Wir bieten kompetenten Service und Beratung, die den optimalen Betrieb Ihrer Ausrüstung sichern. Wir finden immer einen Weg, Ihre technischen Herausforderungen zu lösen, sei es bei der laufenden Wartung oder bei akuten Reparaturen.',
        'Haben Sie die Idee, haben wir die Lösung – wir kombinieren unsere technische Kompetenz mit einem praktischen Ansatz, um die besten Ergebnisse für unsere Kunden zu erzielen.'
      ]
    },
    'teknisk-radgivning': {
      da: [
        'Vi yder teknisk rådgivning og sparring i hele processen fra idé til drift.',
        'Vores ekspertise dækker alle tekniske aspekter af skinnekørende materiel.',
        'Vi hjælper med at finde de rette løsninger to jeres specifikke udfordringer.',
        'Unik viden og specialiseret sparring til jeres projekter.'
      ],
      en: [
        'We provide technical advisory and sparring throughout the process from idea to operation.',
        'Our expertise covers all technical aspects of rail-bound equipment.',
        'We help find the right solutions for your specific challenges.',
        'Unique knowledge and specialized sparring for your projects.'
      ],
      de: [
        'Wir bieten technische Beratung und Unterstützung im gesamten Prozess von der Idee bis zum Betrieb.',
        'Unsere Kompetenz deckt alle technischen Aspekte von schienengebundenen Fahrzeugen ab.',
        'Wir helfen Ihnen, die richtigen Lösungen für Ihre spezifischen Herausforderungen zu finden.',
        'Einzigartiges Wissen und spezialisierte Unterstützung für Ihre Projekte.'
      ]
    },
    'salg-og-kob': {
      da: [
        'Vi formidler køb og salg af skinnekørende arbejdsmaskiner og relevant udstyr.',
        'Gennem vores store netværk i Europa kan vi finde det rette materiel til jeres behov.',
        'Vi rådgiver om indkøb af maskiner, så I får den mest optimale løsning.',
        'Professionel håndtering af hele handelsforløbet.'
      ],
      en: [
        'We facilitate the purchase and sale of rail-bound work machines and relevant equipment.',
        'Through our large network in Europe, we can find the right equipment for your needs.',
        'We advise on the purchase of machines so you get the most optimal solution.',
        'Professional handling of the entire trading process.'
      ],
      de: [
        'Wir vermitteln den Kauf und Verkauf von schienengebundenen Arbeitsmaschinen und relevanter Ausrüstung.',
        'Durch unser großes Netzwerk in Europa können wir die passende Ausrüstung für Ihre Bedürfnisse finden.',
        'Wir beraten Sie beim Kauf von Maschinen, damit Sie die optimalste Lösung erhalten.',
        'Professionelle Abwicklung des gesamten Handelsprozesses.'
      ]
    },
    'ecm-certifikat': {
      da: [
        'Rail & Maskinteknik ApS er certificeret i henhold til ECM (Entity in Charge of Maintenance).',
        'Vi overholder alle gældende EU-regler og standarder for vedligeholdelse af jernbanemateriel.',
        'Certificeringen er jeres garanti for høj kvalitet og sikkerhed i vores arbejde.',
        'Vi arbejder løbende på at opretholde og forbedre vores tekniske standarder.'
      ],
      en: [
        'Rail & Maskinteknik ApS is certified according to ECM (Entity in Charge of Maintenance).',
        'We comply with all applicable EU regulations and standards for maintenance of railway equipment.',
        'The certification is your guarantee of high quality and safety in our work.',
        'We continuously work to maintain and improve our technical standards.'
      ],
      de: [
        'Rail & Maskinteknik ApS ist nach ECM (Entity in Charge of Maintenance) zertifiziert.',
        'Wir halten alle geltenden EU-Vorschriften und Standards für die Wartung von Eisenbahnfahrzeugen ein.',
        'Die Zertifizierung ist Ihre Garantie für hohe Qualität und Sicherheit bei unserer Arbeit.',
        'Wir arbeiten kontinuierlich daran, unsere technischen Standards aufrechtzuerhalten und zu verbessern.'
      ]
    }
  };

  const pageContent = contentMap[identifier] || { da: [], en: [], de: [] };

  const defaultTitles = {
    'service': { da: 'Service', en: 'Service', de: 'Service' },
    'teknisk-radgivning': { da: 'Teknisk rådgivning', en: 'Technical Advisory', de: 'Technische Beratung' },
    'salg-og-kob': { da: 'Salg og køb', en: 'Sales and Purchase', de: 'Verkauf und Kauf' },
    'ecm-certifikat': { da: 'ECM certifikat', en: 'ECM Certificate', de: 'ECM-Zertifikat' }
  };
  const defaultTitle = defaultTitles[identifier] || { da: 'Service', en: 'Service' };

  return (
    <main>
      <SEO 
        title={page ? t(page.title) : t(defaultTitle)} 
        description={page ? t(page.hero_subtitle) : ''} 
      />
      <Hero 
        title={page?.title || defaultTitle} 
        subtitle={page?.hero_subtitle || { da: '', en: '' }}
        image={service?.image?.[0] || (identifier === 'ecm-certifikat' ? 'https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/bb5a0a90-ba72-4c16-ac34-070262d287ba.jpg' : null)}
        isSubpage={true}
      />

      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="space-y-8">
                <h2 className="text-3xl font-bold text-slate-900 tracking-tight">
                  {identifier === 'service' 
                    ? t({ da: 'Service og vedligeholdelse', en: 'Service and Maintenance', de: 'Service und Wartung' }) 
                    : identifier === 'teknisk-radgivning'
                    ? t({ da: 'Teknisk rådgivning', en: 'Technical Advisory', de: 'Technische Beratung' })
                    : identifier === 'salg-og-kob'
                    ? t({ da: 'Salg og køb', en: 'Sales and Purchase', de: 'Verkauf und Kauf' })
                    : t({ da: 'Vores tilgang', en: 'Our Approach', de: 'Unser Ansatz' })}
                </h2>
                
                {identifier === 'service' ? (
                  <div className="space-y-6 text-lg text-slate-700 leading-relaxed">
                    <p>
                      {t({
                        da: 'Rail & Maskinteknik ApS har mange års erfaring med servicering og drift af alle typer skinnekørende arbejdsmaskiner.',
                        en: 'Rail & Maskinteknik ApS has many years of experience with servicing and operation of all types of rail-bound work machines.',
                        de: 'Rail & Maskinteknik ApS verfügt über langjährige Erfahrung in der Wartung und im Betrieb aller Arten von schienengebundenen Arbeitsmaschinen.'
                      })}
                    </p>
                    <p>
                      {t({
                        da: 'Vi tilbyder bl.a.:',
                        en: 'We offer among other things:',
                        de: 'Wir bieten unter anderem:'
                      })}
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                      {[
                        { da: 'Teknisk eftersyn', en: 'Technical inspection', de: 'Technische Inspektion' },
                        { da: 'Årligt eftersyn', en: 'Annual inspection', de: 'Jährliche Inspektion' },
                        { da: 'Revision', en: 'Revision', de: 'Revision' },
                        { da: 'Eftersyn efter uheld', en: 'Inspection after accidents', de: 'Inspektion nach Unfällen' },
                        { da: 'Opmåling af hjulprofil', en: 'Measurement of wheel profile', de: 'Messung des Radprofils' },
                        { da: 'Renovering og opgradering', en: 'Renovation and upgrading', de: 'Renovierung und Modernisierung' },
                        { da: 'Serviceaftale', en: 'Service agreement', de: 'Servicevertrag' },
                        { da: 'Tilkaldevagt 24/7', en: 'On-call service 24/7', de: 'Bereitschaftsdienst 24/7' },
                        { da: 'Funktions afprøvning', en: 'Functional testing', de: 'Funktionsprüfung' },
                        { da: 'Undervisning i Litra', en: 'Training in Litra', de: 'Schulung in Litra' }
                      ].map((item, i) => (
                        <li key={i}>{t(item)}</li>
                      ))}
                    </ul>
                  </div>
                ) : identifier === 'teknisk-radgivning' ? (
                  <div className="space-y-6 text-lg text-slate-700 leading-relaxed">
                    <p>
                      {t({
                        da: 'Vi tilbyder bl.a.:',
                        en: 'We offer among other things:',
                        de: 'Wir bieten unter anderem:'
                      })}
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                      {[
                        { da: 'Kravspecificering', en: 'Requirement specification', de: 'Anforderungsspezifikation' },
                        { da: 'Teknisk gennemgang og besigtigelse', en: 'Technical review and inspection', de: 'Technische Überprüfung und Besichtigung' },
                        { da: 'Udarbejdelse af farelog', en: 'Preparation of hazard log', de: 'Erstellung des Gefahrenprotokolls' },
                        { da: 'Udarbejdelse af Systemdefinition', en: 'Preparation of System Definition', de: 'Erstellung der Systemdefinition' },
                        { da: 'Signifikantvurdering', en: 'Significance assessment', de: 'Signifikanzbewertung' },
                        { da: 'Vurdering og udvælgelse af sikkerhedskritiske komponenter', en: 'Assessment and selection of safety-critical components', de: 'Bewertung und Auswahl sicherheitskritischer Komponenten' },
                        { da: 'Markedsundersøgelse', en: 'Market research', de: 'Marktforschung' },
                        { da: 'Evaluering af tilbud', en: 'Evaluation of offers', de: 'Angebotsbewertung' },
                        { da: 'Udvælgelse af leverandør', en: 'Selection of supplier', de: 'Lieferantenauswahl' },
                        { da: 'Forhånds dialog med leverandør', en: 'Preliminary dialogue with supplier', de: 'Vordialog mit dem Lieferanten' },
                        { da: '3. part i garantisager', en: '3rd party in warranty cases', de: 'Dritter in Gewährleistungsfällen' },
                        { da: 'Teknisk support ved renovering og ombygning', en: 'Technical support for renovation and rebuilding', de: 'Technische Unterstützung bei Renovierung und Umbau' }
                      ].map((item, i) => (
                        <li key={i}>{t(item)}</li>
                      ))}
                    </ul>
                    <div className="mt-8 rounded-2xl overflow-hidden border border-slate-100">
                      <img 
                        src="https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/45554566-9650-446e-a9fa-00f4228f236b.jpg" 
                        alt="Teknisk rådgivning" 
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  </div>
                ) : identifier === 'salg-og-kob' ? (
                  <div className="space-y-6 text-lg text-slate-700 leading-relaxed">
                    <p>
                      {t({
                        da: 'Rail & Maskinteknik ApS hjælper jer gerne, hvis i overvejer at købe eller sælge materiel, vi kan også være behjælpelige med at skaffe nye eller renoveret reservedele til jeres materiel.',
                        en: 'Rail & Maskinteknik ApS is happy to help if you are considering buying or selling equipment. We can also assist in obtaining new or refurbished spare parts for your equipment.',
                        de: 'Rail & Maskinteknik ApS hilft Ihnen gerne, wenn Sie den Kauf oder Verkauf von Ausrüstung in Betracht ziehen. Wir können auch bei der Beschaffung neuer oder überholter Ersatzteile für Ihre Ausrüstung behilflich sein.'
                      })}
                    </p>
                    <div className="mt-8 rounded-2xl overflow-hidden border border-slate-100">
                      <img 
                        src="https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/7b467390-e266-4d0a-854c-cd8d86d7e5d1.png" 
                        alt="Salg og køb" 
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {t(pageContent).map((text, index) => (
                      <p key={index} className="text-lg text-slate-700 leading-relaxed">
                        {text}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                {identifier === 'service' ? (
                  <div className="space-y-6">
                    <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-100">
                      <img 
                        src="https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/4e92d387-0414-4b1e-9104-2e8febd1f545.jpg" 
                        alt="Service af maskiner" 
                        className="w-full h-64 object-cover"
                      />
                    </div>
                    <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-100">
                      <img 
                        src="https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/faf46c14-74e7-4807-9fdb-05a07854c758.jpg" 
                        alt="Vedligeholdelse" 
                        className="w-full h-64 object-cover"
                      />
                    </div>
                  </div>
                ) : identifier === 'ecm-certifikat' ? (
                  <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-100">
                    <img 
                      src="https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/bb5a0a90-ba72-4c16-ac34-070262d287ba.jpg" 
                      alt="ECM Certifikat" 
                      className="w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setIsLightboxOpen(true)}
                    />
                  </div>
                ) : (
                  <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 h-fit sticky top-32">
                    <h3 className="text-xl font-bold text-slate-900 mb-4">
                      {t({ da: 'Har I brug for sparring?', en: 'Need sparring?', de: 'Benötigen Sie Beratung?' })}
                    </h3>
                    <p className="text-slate-600 mb-8">
                      {t({ 
                        da: 'Vi står klar til at hjælpe jer med jeres tekniske udfordringer og projekter.', 
                        en: 'We are ready to help you with your technical challenges and projects.',
                        de: 'Wir sind bereit, Ihnen bei Ihren technischen Herausforderungen und Projekten zu helfen.' 
                      })}
                    </p>
                    <Link 
                      to={getLocalizedPath('/kontakt')} 
                      className="inline-flex items-center justify-center px-6 py-3 bg-[#f59c00] text-white font-bold rounded-xl hover:bg-[#e08e00] transition-all"
                    >
                      {t({ da: 'Kontakt os i dag', en: 'Contact us today', de: 'Kontaktieren Sie uns heute' })}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {isLightboxOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setIsLightboxOpen(false)}
        >
          <div className="relative max-w-5xl w-full max-h-full flex items-center justify-center">
            <button 
              className="absolute -top-12 right-0 text-white hover:text-slate-300 p-2"
              onClick={() => setIsLightboxOpen(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <img 
              src="https://assets.auxodev.dk/assets/488e7d70-fe21-4956-bd96-e0986a28cf0b/bb5a0a90-ba72-4c16-ac34-070262d287ba.jpg" 
              alt="ECM Certifikat" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </main>
  );
};

export default ServicePage;
